import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { ticketAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Ticket, Plus, MessageSquare, Clock, CheckCircle, XCircle, Send, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = { open: 'bg-green-500/15 text-green-400', resolved: 'bg-emerald-500/15 text-emerald-400', closed: 'bg-zinc-500/15 text-[var(--text-secondary)]' };
const priorityColors = { low: 'text-zinc-400', medium: 'text-amber-400', high: 'text-red-400' };

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMsg, setReplyMsg] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'general', message: '', priority: 'medium' });
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = async () => {
    try { const { data } = await ticketAPI.getAll(); setTickets(data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error('Please fill subject and message'); return; }
    setSubmitting(true);
    try {
      await ticketAPI.create(form);
      toast.success('Ticket created');
      setShowForm(false);
      setForm({ subject: '', category: 'general', message: '', priority: 'medium' });
      fetchTickets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create ticket'); }
    setSubmitting(false);
  };

  const handleReply = async () => {
    if (!replyMsg.trim()) return;
    try {
      await ticketAPI.reply(selectedTicket.id, { message: replyMsg });
      setReplyMsg('');
      const { data } = await ticketAPI.get(selectedTicket.id);
      setSelectedTicket(data);
      toast.success('Reply sent');
    } catch { toast.error('Failed to send reply'); }
  };

  if (selectedTicket) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl space-y-4" data-testid="ticket-detail">
          <button onClick={() => setSelectedTicket(null)} className="flex items-center gap-1.5 text-sm text-green-400 hover:text-green-300" data-testid="back-to-tickets">
            <ChevronLeft className="w-4 h-4" /> Back to Tickets
          </button>
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-[var(--text-primary)]">{selectedTicket.subject}</p>
                <p className="text-[10px] font-mono text-[var(--text-secondary)]">{selectedTicket.ticket_number}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[selectedTicket.status] || ''}`}>{selectedTicket.status}</span>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {(selectedTicket.messages || []).map((m, i) => (
                <div key={i} className={`p-3 rounded-lg text-sm ${m.sender === 'user' ? 'bg-green-500/10 border border-green-500/20 ml-4' : 'bg-[var(--bg-primary)] border border-[var(--border-subtle)] mr-4'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-medium text-[var(--text-secondary)]">{m.sender === 'user' ? 'You' : 'Support'}</span>
                    <span className="text-[10px] text-[var(--text-tertiary)]">{new Date(m.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                  </div>
                  <p className="text-[var(--text-primary)]">{m.message}</p>
                </div>
              ))}
            </div>
            {selectedTicket.status === 'open' && (
              <div className="mt-4 flex gap-2">
                <input value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} placeholder="Type a reply..."
                  data-testid="ticket-reply-input"
                  className="flex-1 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleReply()} />
                <button onClick={handleReply} data-testid="ticket-send-reply" className="p-2.5 bg-green-500 hover:bg-green-600 text-white rounded transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6" data-testid="tickets-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Support Tickets</h1>
              <p className="text-xs text-[var(--text-secondary)]">Get help from our support team</p>
            </div>
          </div>
          <button onClick={() => setShowForm(!showForm)} data-testid="create-ticket-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all">
            <Plus className="w-4 h-4" /> New Ticket
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleCreate} className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="ticket-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject"
                  placeholder="Brief description" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ticket-category"
                  className="w-full h-10 bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] rounded px-3 text-sm">
                  <option value="general">General</option>
                  <option value="trading">Trading</option>
                  <option value="deposit">Deposit/Withdrawal</option>
                  <option value="kyc">KYC</option>
                  <option value="technical">Technical Issue</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[var(--text-secondary)]">Priority</Label>
              <div className="flex gap-2">
                {['low', 'medium', 'high'].map((p) => (
                  <button type="button" key={p} onClick={() => setForm({ ...form, priority: p })} data-testid={`ticket-priority-${p}`}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${form.priority === p ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'}`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[var(--text-secondary)]">Message</Label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message"
                placeholder="Describe your issue in detail..." rows={4}
                className="w-full bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] rounded p-3 text-sm placeholder:text-[var(--text-tertiary)] resize-none focus:border-green-500 outline-none" />
            </div>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-[var(--text-secondary)]">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="ticket-submit-btn"
                className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-50">
                {submitting ? 'Creating...' : 'Submit Ticket'}
              </button>
            </div>
          </form>
        )}

        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">Loading...</div>
          ) : tickets.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No tickets yet. Create one if you need help.</div>
          ) : (
            <div className="divide-y divide-[var(--divide-color)]">
              {tickets.map((t) => (
                <button key={t.id} onClick={() => setSelectedTicket(t)} data-testid={`ticket-item-${t.id}`}
                  className="w-full p-4 flex items-center gap-4 hover:bg-[var(--hover-bg)] transition-colors text-left">
                  <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">{t.subject}</p>
                      <span className={`text-[10px] font-medium ${priorityColors[t.priority] || ''}`}>{t.priority}</span>
                    </div>
                    <p className="text-[10px] text-[var(--text-secondary)]">{t.ticket_number} &middot; {t.category} &middot; {new Date(t.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${statusColors[t.status] || ''}`}>{t.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
